import type { HitProps, PriceGroups } from '@components/algolia/Hit';
import { colourKeyForVariantSku } from '@utils/algolia/colourKeyForVariantSku';
import type { BundlePromotion } from '@adapters/bigcommerce/transforms/bundlePromotion';

export type PriceGroupKey = keyof PriceGroups;

export interface BundleLinePrice {
  current: number;
  original: number;
  isRange: boolean;
}

export interface BundleLineQuote extends BundleLinePrice {
  quantity: number;
}

export interface BundleTotals {
  current: number;
  original: number;
  savings: number;
  isFromPrice: boolean;
}

const round = (n: number): number => Math.round(n * 100) / 100;

const getColourKeyLinePrice = (
  record: HitProps,
  colourKey: string,
  priceGroupKey: PriceGroupKey
): BundleLinePrice => {
  const variant = record.variants?.[colourKey];
  const group = variant?.prices?.[priceGroupKey];

  const price = group?.price ?? variant?.price ?? record.price ?? 0;
  const salePrice = group
    ? group.salePrice ?? 0
    : variant?.salePrice ?? record.salePrice ?? 0;
  const range = group?.priceRange ?? variant?.priceRange;

  const original = range?.price?.[0] ?? price;
  const saleLow = range?.salePrice?.[0] ?? salePrice;
  const isRange = Boolean(
    (range?.price && range.price[0] !== range.price[1]) ||
      (range?.salePrice && range.salePrice[0] !== range.salePrice[1])
  );

  return {
    current: saleLow > 0 && saleLow < original ? saleLow : original,
    original,
    isRange,
  };
};

export const getBundleLinePrice = (
  record: HitProps,
  variantSku: string | null,
  priceGroupKey: PriceGroupKey
): BundleLinePrice =>
  getColourKeyLinePrice(
    record,
    colourKeyForVariantSku(record, variantSku),
    priceGroupKey
  );

export const getColourwayLinePrice = (
  record: HitProps,
  colourName: string | undefined,
  priceGroupKey: PriceGroupKey
): BundleLinePrice | null => {
  const target = colourName?.trim().toLowerCase();
  const colourKey = Object.keys(record.variants ?? {}).find(
    (key) => key.trim().toLowerCase() === target
  );
  return colourKey
    ? getColourKeyLinePrice(record, colourKey, priceGroupKey)
    : null;
};

export const sumBundleLines = (lines: BundleLineQuote[]): BundleTotals => {
  const current = round(
    lines.reduce((sum, line) => sum + line.current * line.quantity, 0)
  );
  const original = round(
    lines.reduce((sum, line) => sum + line.original * line.quantity, 0)
  );

  return {
    current,
    original,
    savings: round(Math.max(0, original - current)),
    isFromPrice: lines.some((line) => line.isRange),
  };
};

const GUEST_CUSTOMER_GROUP_ID = 0;

export const isBundlePromotionLive = (
  promotion: BundlePromotion,
  now: Date,
  customerGroupId: number | null | undefined
): boolean => {
  const groupId = customerGroupId || GUEST_CUSTOMER_GROUP_ID;
  const { startsAt, endsAt, customerGroupIds, excludedCustomerGroupIds } =
    promotion;

  return (
    (!startsAt || new Date(startsAt) <= now) &&
    (!endsAt || now < new Date(endsAt)) &&
    (customerGroupIds.length === 0 || customerGroupIds.includes(groupId)) &&
    !excludedCustomerGroupIds.includes(groupId)
  );
};

export const applyBundlePromotion = <Line extends BundleLinePrice>(
  lines: Line[],
  promotion: Pick<BundlePromotion, 'percentOff' | 'excludeItemsOnSale'> | null
): Line[] =>
  promotion
    ? lines.map((line) =>
        promotion.excludeItemsOnSale && line.current < line.original
          ? line
          : {
              ...line,
              current: round(line.current * (1 - promotion.percentOff / 100)),
            }
      )
    : lines;
